import { withStoredValue } from '../shared/selectOptions';

/**
 * Country and State are one fact about an address, and keeping them coherent takes
 * more than narrowing one by the other. Both halves live here because the form reads
 * them and the form's state hook writes them.
 */

/**
 * The subdivisions to offer for the chosen country, always including the one on file.
 *
 * Narrowing by country is what the design's two selects imply, but it cannot be the
 * whole story: `users.country_code` is nullable and most rows have no country at all,
 * so narrowing alone would leave a user with a state and no country showing a blank
 * State field while still holding — and re-saving — the stored value. The same goes
 * for a country whose subdivision list does not contain what is on file.
 *
 * So the stored value is added back when the country's list is missing it, preferring
 * its proper label from whichever country does list it and falling back to the raw
 * value. The field then shows what the record actually holds, which is what the legacy
 * form does by never narrowing at all (see users/_shared_form, which passes no
 * :country).
 */
export function stateOptions(states = {}, countryCode, current) {
  const forCountry = states[countryCode] || [];

  if (!current || forCountry.some(({ value }) => value === current)) {
    return forCountry;
  }

  const stored = []
    .concat(...Object.values(states))
    .find(({ value }) => value === current);

  return stored
    ? [stored, ...forCountry]
    : withStoredValue(forCountry, current);
}

/**
 * Whether choosing `countryCode` should abandon the state on file.
 *
 * Only when the new country genuinely does not have it. Clearing on every country
 * change looks equivalent but destroys data in the one case stateOptions exists to
 * protect: a user with `state: 'TX'` and no country renders "Texas", and an admin
 * setting Country to United States is *completing* that record, not replacing it.
 * Blanking Texas there would lose the state and, since Geography#can_geocode? wants a
 * city and a state, leave the address unplaceable.
 *
 * No country at all is that same case arriving from the other direction. The Country
 * select carries a clear button, and clearing it reports `''` like any other change —
 * so without the blank check here, an admin who cleared Country would silently lose
 * the state as well, which is precisely the record stateOptions goes on to render.
 */
export function abandonsState(states = {}, countryCode, current) {
  if (!current || !countryCode) return false;

  return !(states[countryCode] || []).some(({ value }) => value === current);
}
